<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, reactive, ref, watchEffect, watch } from "vue";
import { ElInput } from "element-plus";
import type { Visit, VisitWithFields } from "@/api/visits";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import InductionStatus from "@/pages/visitors/components/InductionStatus.vue";
import type { VisitorCategory } from "@/api/visitorCategories";
import { useVisitorApi } from "@/composables/visitors";
import type { VISIT_TYPE } from "@/stores/constants";

interface Props {
  id?: string;
  visit?: Visit;
  visitWithFields?: VisitWithFields;
  metadata?: {
    notes: string;
  };
  selectedCategory?: VisitorCategory;
  visitType?: VISIT_TYPE;
}

const item = computed(() => {
  if (props.visit) return props.visit;

  if (props.visitWithFields) {
    return {
      id: `${props.visitWithFields.visitId}`,
      pk: props.visitWithFields.id,
      full_name: props.visitWithFields.fullName,
      agreement_id: props.visitWithFields.agreementId ?? "",
      metadata: JSON.stringify({
        notes: props.visitWithFields.notes,
      }),
      location: {
        time_zone: props.visitWithFields.location?.timeZone,
      },
    };
  }

  return undefined;
});

const { t } = useI18n();
const emit = defineEmits<{
  (event: "update-metadata", payload: { notes: string }): void;
}>();

const props = defineProps<Props>();

const metadata = reactive({
  notes: props.metadata?.notes ?? "",
});

const timezone = computed(() => {
  if (!item.value) return "";
  return item.value?.location?.time_zone ?? "";
});

const isAgreementLoading = ref(false);
const isContactTraceLoading = ref(false);

const { contactTrace, exportVisitorAgreement } = useVisitorApi();

const handleDownloadVisitorAgreement = async () => {
  if (!item.value?.full_name) return;

  isAgreementLoading.value = true;

  await exportVisitorAgreement({
    visitPk: item.value.pk,
    name: item.value.full_name,
  });

  isAgreementLoading.value = false;
};

const handleDownloadContactTrace = async () => {
  if (!item.value?.id || !item.value?.full_name) return;

  isContactTraceLoading.value = true;

  await contactTrace({
    visitId: item.value.id,
    name: item.value.full_name,
  });

  isContactTraceLoading.value = false;
};

watchEffect(() => {
  if (!item.value) return;

  if (typeof item.value?.metadata === "string") {
    const { notes } = JSON.parse(item.value?.metadata) as {
      notes: string;
    };

    if (!notes) {
      metadata.notes = "";
      return;
    }

    metadata.notes = notes;
  }
});

watch(
  () => props.metadata,
  (val) => {
    metadata.notes = val?.notes ?? "";
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="visit-extras">
    <CoreButton
      v-if="item?.agreement_id"
      type="primary"
      text
      :icon="['fal', isAgreementLoading ? 'circle-notch' : 'download']"
      :icon-spin="isAgreementLoading"
      class="visitor-agreement"
      :disabled="isAgreementLoading"
      @click="handleDownloadVisitorAgreement"
    >
      {{ t("visitors.settings.downloadVisitorAgreement") }}
    </CoreButton>

    <CoreButton
      type="primary"
      text
      class="contact-trace"
      :icon="['fal', isContactTraceLoading ? 'circle-notch' : 'download']"
      :icon-spin="isContactTraceLoading"
      :disabled="isContactTraceLoading"
      @click="handleDownloadContactTrace"
    >
      {{ t("visitors.settings.contactTrace") }}
    </CoreButton>

    <InductionStatus
      :id="id ?? item?.pk ?? ''"
      :timezone="timezone"
      :selected-category="props.selectedCategory"
      :type="props.visitType"
    />

    <div class="notes">
      <p
        class="notes-title"
        v-text="t('visitors.settings.yourNotes')"
      />
      <div class="notes-textarea">
        <el-input
          v-model="metadata.notes"
          type="textarea"
          resize="vertical"
          class="notes-textarea"
          rows="5"
          :placeholder="t('visitors.settings.startTyping')"
          @input="emit('update-metadata', metadata)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.so-button {
  margin-left: 0;
  margin-bottom: 12px;
}

.so-textarea {
  width: 100%;
}

.visit-extras {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 14px;
}

.notes {
  width: 100%;
}

.notes-textarea {
  position: relative;
}

.so-button.contact-trace {
  margin-left: 0;
}

.visitor-agreement {
  margin: 0;
}
</style>
